import { authProvider } from './AuthProvider';
import { PaymentRequestInfo } from '../gql-types.generated';
import { PaymentsDueRowData } from '../custom-types/PaymentsDueRowData';
import { MSAL_CLIENT_ID } from './Constants';

interface StandardRequestHeaders {
  authorization?: string;
  'x-ezpay-pmt': string;
}

export const getStandardHeaders = (): StandardRequestHeaders => {
  const headers: StandardRequestHeaders = {
    'x-ezpay-pmt': MSAL_CLIENT_ID,
  };
  return headers;
};

export const getStandardHeadersWithAuth = async (): Promise<StandardRequestHeaders> => {
  const tokenResponse = await authProvider.getAccessToken();
  const headers: StandardRequestHeaders = {
    authorization: `Bearer ${tokenResponse.accessToken}`,
    'x-ezpay-pmt': MSAL_CLIENT_ID,
  };

  return headers;
};

/**
 * The attempt discriminator in a createPayment idempotency key.
 *
 * The key has to stay the same across a double submit of ONE attempt, so that the API deduplicates it, and has to
 * change for a genuine SECOND attempt on the same request - otherwise the payer is locked out for the 24 hours the
 * key is retained. The count of payments ever attached to the request is the only value that does both, because it
 * is the only one that never goes backwards: a void leaves the canceled payment counted while restoring every
 * reversible quantity - totalPaid, totalDue, the amount - to what the first attempt already keyed on.
 *
 * Defaults to 0 rather than throwing. A missing count means an API that predates the field, and the pre-existing
 * hardcoded 0 is exactly what it used to send, so the worst case is the old behaviour rather than a broken payment.
 */
const attemptOf = (paymentCount: number | null | undefined): number => paymentCount ?? 0;

interface CreatePaymentHeaders {
  authorization?: string;
  'x-ezpay-pmt': string;
  'x-aptean-tenant': string;
  'idempotency-key': string;
}

export const getCreatePaymentHeaders = async (paymentRequestInfo: PaymentRequestInfo): Promise<CreatePaymentHeaders> => {
  const headers: CreatePaymentHeaders = {
    'x-ezpay-pmt': MSAL_CLIENT_ID,
    'x-aptean-tenant': paymentRequestInfo.tenantId,
    'idempotency-key': `${paymentRequestInfo.paymentRequestId}~${attemptOf(paymentRequestInfo.paymentCount)}`,
  };

  return headers;
};

export const getCreatePaymentHeadersWithAuth = async (paymentRequestInfo: PaymentRequestInfo): Promise<CreatePaymentHeaders> => {
  const tokenResponse = await authProvider.getAccessToken();
  const headers: CreatePaymentHeaders = {
    authorization: `Bearer ${tokenResponse.accessToken}`,
    'x-ezpay-pmt': MSAL_CLIENT_ID,
    'x-aptean-tenant': paymentRequestInfo.tenantId,
    'idempotency-key': `${paymentRequestInfo.paymentRequestId}~${attemptOf(paymentRequestInfo.paymentCount)}`,
  };

  return headers;
};

export const getCreateConsolidatedOrPartialPaymentHeadersWithAuth = async (
  tenantId: string,
  paymentRequestInfoList: PaymentsDueRowData[],
): Promise<CreatePaymentHeaders> => {
  let idempotencyKey = '';
  const list = [...paymentRequestInfoList].sort((a, b) => {
    const refNumberA = a.paymentDue.paymentRequest?.referenceNumber;
    const refNumberB = b.paymentDue.paymentRequest?.referenceNumber;
    return refNumberA && refNumberB ? refNumberA.localeCompare(refNumberB) : 0;
  });
  list.forEach(p => {
    const referenceNumber = p.paymentDue.paymentRequest?.referenceNumber;
    const totalDue = p.paymentDue.paymentRequest?.totalDue;
    if (referenceNumber && totalDue) {
      // The amount alone cannot separate two attempts: a void restores totalDue to what the first attempt keyed on,
      // which is how the reported collision was produced. The attempt count is carried per request, since a
      // consolidated payment can reopen one of its requests without touching the others.
      //
      // It goes INSIDE the angle brackets deliberately. These are concatenated with no separator between requests, so
      // an unclosed trailing count would run into the next reference number - 1401<1400>~1 followed by 1402<2200>~0
      // reads the same as 1401<1400>~11 followed by 402<2200>~0, two different states sharing one key.
      idempotencyKey += `${referenceNumber}<${totalDue}~${attemptOf(p.paymentDue.paymentRequest?.payments?.length)}>`;
    }
  });
  const tokenResponse = await authProvider.getAccessToken();
  const headers: CreatePaymentHeaders = {
    authorization: `Bearer ${tokenResponse.accessToken}`,
    'x-ezpay-pmt': MSAL_CLIENT_ID,
    'x-aptean-tenant': tenantId,
    'idempotency-key': `${idempotencyKey}`,
  };

  return headers;
};

interface TenantHeaders {
  authorization?: string;
  'x-ezpay-pmt': string;
  'x-aptean-tenant': string;
}

export const getTenantHeaders = (tenantId: string): TenantHeaders => {
  const headers: TenantHeaders = {
    'x-ezpay-pmt': MSAL_CLIENT_ID,
    'x-aptean-tenant': tenantId,
  };
  return headers;
};

export const getTenantHeadersWithAuth = async (tenantId: string): Promise<TenantHeaders> => {
  const tokenResponse = await authProvider.getAccessToken();
  const headers: TenantHeaders = {
    authorization: `Bearer ${tokenResponse.accessToken}`,
    'x-ezpay-pmt': MSAL_CLIENT_ID,
    'x-aptean-tenant': tenantId,
  };
  return headers;
};
